import { ErrorObject } from 'ajv';

import { ValidateFunction } from './types/ValidateFunction';
import {
  DependencyError,
  MaximumError,
  MaxItemsError,
  MaxLengthError,
  MinimumError,
  MinItemsError,
  MinLengthError,
} from './types/ValidationError';
import { ValidationSummary } from './types/ValidationSummary';

import {
  isCustomError,
  isDependencyError,
  isEnumError,
  isFormatError,
  isMaximumError,
  isMaxItemsError,
  isMaxLengthError,
  isMinimumError,
  isMinItemsError,
  isMinLengthError,
  isPatternError,
  isRequiredError,
  isUniqueError,
} from './validationErrorHelpers';

/**
 * Checks if the provided `candidate` is of type `T`.
 * Uses the provided `validator` to ensure `candidate` matches the associated JSON Schema.
 * @param validator validation function for the associated schema.
 * @param candidate entity to validate.
 * @returns true if `candidate` is valid, and casts to provided Type.
 */
export function isValid<T>(validator: ValidateFunction<T>, candidate: any): candidate is T {
  return validator(candidate) === true;
}

/**
 * Groups AJV Validation Errors by PropertyName
 * @param errors
 */
export function groupValidationErrors(errors: ErrorObject[] | undefined | null): ValidationSummary {
  if (!errors) {
    return {};
  }

  const validationSummary: ValidationSummary = {};

  errors.forEach(p => processError(p, validationSummary));

  return validationSummary;
}

function processError(error: ErrorObject, validationSummary: ValidationSummary): void {
  const propertyName = getMissingPropertyName(error);
  let message = error.message;

  if (!propertyName) {
    console.log('Failed to parse property name for error', error);
    return;
  }

  if (isRequiredError(error)) {
    processRequiredError(propertyName, validationSummary);
    return;
  }

  if (isDependencyError(error)) {
    processDependencyError(propertyName, error, validationSummary);
    return;
  }

  if (
    isMinimumError(error) ||
    isMaximumError(error) ||
    isMinLengthError(error) ||
    isMaxLengthError(error) ||
    isMinItemsError(error) ||
    isMaxItemsError(error)
  ) {
    processComparisonError(propertyName, error, validationSummary);
    return;
  }

  if (message) {
    addError(validationSummary, propertyName, message);
    return;
  }

  console.log('Processing failed for error ', error);
}

function processRequiredError(propertyName: string, validationSummary: ValidationSummary): void {
  const message = `field required`;
  addError(validationSummary, propertyName, message);
}

function processDependencyError(
  propertyName: string,
  error: DependencyError,
  validationSummary: ValidationSummary
): void {
  const message = error.message ? error.message : `${propertyName} is missing a dependency.`;
  addError(validationSummary, propertyName, message);
}

function processComparisonError(
  propertyName: string,
  error: MinimumError | MaximumError | MinLengthError | MaxLengthError | MinItemsError | MaxItemsError,
  validationSummary: ValidationSummary
): void {
  const message = error.message
    ? error.message.replace('>=', 'greater than or equal to').replace('<=', 'less than or equal to')
    : `${propertyName} failed a comparison constraint.`;

  addError(validationSummary, propertyName, message);
}

function addError(summary: ValidationSummary, propertyName: string, message: string): void {
  const current = summary[propertyName];

  if (current) {
    summary[propertyName] = [message, ...current];
  } else {
    summary[propertyName] = [message];
  }
}

function getMissingPropertyName(error: ErrorObject): string | undefined {
  if (typeof error.propertyName === 'string') {
    return error.propertyName;
  }

  if (isRequiredError(error)) {
    return error.params.missingProperty;
  }

  if (isDependencyError(error)) {
    return error.params.missingProperty;
  }

  if (
    isEnumError(error) ||
    isFormatError(error) ||
    isCustomError(error) ||
    isMinimumError(error) ||
    isMaximumError(error) ||
    isMinLengthError(error) ||
    isMaxLengthError(error) ||
    isMinItemsError(error) ||
    isMaxItemsError(error) ||
    isUniqueError(error) ||
    isPatternError(error)
  ) {
    if (error.dataPath) {
      return getPropertyNameFromPath(error);
    }
  }

  return undefined;
}

function getPropertyNameFromPath(error: ErrorObject) {
  return error.dataPath.split('/').reverse()[0];
}
